import { useForm, usePage } from "@inertiajs/react"
import { useTranslation } from "react-i18next"
import { ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { FormError } from "@/components/FormError"
import { FlashMessages } from "@/components/FlashMessages"
import { AuthLayout } from "@/components/layouts/AuthLayout"

interface PageProps {
  invitationToken?: string | null
  flash: {
    notice?: string | null
    alert?: string | null
  }
  [key: string]: unknown
}

export default function Login() {
  const { t } = useTranslation()
  const { flash, invitationToken } = usePage<PageProps>().props
  const { data, setData, post, processing, errors } = useForm({
    email: "",
    password: "",
    invitation_token: invitationToken || "",
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post("/login")
  }

  return (
    <AuthLayout>
      <FlashMessages notice={flash?.notice} alert={flash?.alert} />
      <div className="eyebrow mb-2">{t("login.eyebrow", { defaultValue: "Bem-vindo de volta" })}</div>
      <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight text-foreground m-0">
        {t("login.title")}
      </h1>
      <p className="text-muted-foreground text-sm mt-2 mb-7">
        {t("login.subtitle")}
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="email" className="mono-col">{t("login.emailLabel")}</Label>
          <Input
            id="email"
            type="email"
            value={data.email}
            onChange={(e) => setData("email", e.target.value)}
            placeholder={t("login.emailPlaceholder")}
            required
          />
          <FormError error={errors.email} />
        </div>

        <div className="space-y-1.5 relative">
          <Label htmlFor="password" className="mono-col">{t("login.passwordLabel")}</Label>
          <Input
            id="password"
            type="password"
            value={data.password}
            onChange={(e) => setData("password", e.target.value)}
            required
          />
          {/* After the input in the DOM so Tab goes email -> password -> link */}
          <a
            href="/forgot-password"
            className="absolute top-0 right-0 mt-0! text-[12px] font-medium hover:underline"
            style={{ color: "var(--accent)" }}
          >
            {t("login.forgotPassword")}
          </a>
          <FormError error={errors.password} />
        </div>

        <Button type="submit" className="w-full" size="lg" disabled={processing}>
          {processing ? t("login.submitting") : t("login.submit")}
          {!processing && <ArrowRight className="w-4 h-4 ml-2" />}
        </Button>

        <div className="flex items-center gap-3 my-2">
          <div className="flex-1 h-px bg-border" />
          <span className="font-mono text-[10px] uppercase text-muted-foreground tracking-[0.18em]">
            {t("login.or", { defaultValue: "ou" })}
          </span>
          <div className="flex-1 h-px bg-border" />
        </div>

        <p className="text-center text-sm text-muted-foreground">
          {t("login.noAccount")}{" "}
          <a
            href="/register"
            className="font-semibold hover:underline"
            style={{ color: "var(--accent)" }}
          >
            {t("login.signUp")}
          </a>
        </p>
      </form>
    </AuthLayout>
  )
}
